
import React from 'react';
import SEO from '../components/SEO';

const TermsOfService: React.FC = () => {
  return (
    <div className="animate-fade-in-up">
      <SEO 
        title="Terms of Service" 
        description="Legal terms and conditions for the usage of the AviaGov aviation platform." 
      />
      <section className="py-24 px-4 max-w-4xl mx-auto">
        <h1 className="text-4xl lg:text-6xl font-bold font-heading mb-8 text-white">Terms of Service</h1>
        <p className="text-slate-400 mb-12">Effective Date: May 22, 2026</p>

        <div className="prose prose-invert prose-slate max-w-none space-y-8 text-slate-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing or using the AviaGov platform, you agree to be bound by these Terms of Service and all applicable aviation regulations including ICAO Annex 15 and 19 requirements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Platform Usage</h2>
            <p>
              AviaGov is an enterprise tool for aeronautical information management. Unauthorized access, data manipulation, or interference with the platform's integrity is strictly prohibited and may constitute a breach of national security laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Service Availability</h2>
            <p>
              While we strive for 99.99% availability, mission-critical updates and AIRAC cycle processing may require scheduled maintenance. Authorities are notified 48 hours in advance of any planned downtime.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Intellectual Property</h2>
            <p>
              The AviaGov software, algorithms, and 3D UI assets are the property of AviaGov Aviation Systems Ltd. Users retain full ownership of the aeronautical data uploaded to their respective instances.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Limitation of Liability</h2>
            <p>
              AviaGov provides tools for data management. The final validation and legal responsibility for aeronautical publications (eAIP, NOTAMs) remains with the licensed authority or ANSP.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. FlyMate AI Services and Uploaded Files</h2>
            <p>
              FlyMate is an AI-assisted aviation information tool. FlyMate outputs are provided for research, drafting, review, and operational support only. They are not a substitute for professional aviation judgment, approved procedures, competent authority review, or legally required validation.
            </p>
            <p>
              Paid FlyMate services may allow users to upload files so that FlyMate can extract readable text and use it as user-provided context. You remain responsible for ensuring that you have the right to upload, process, and use any file submitted to FlyMate. Do not upload classified information, export-controlled information, state secrets, security-sensitive aviation data, personal data that is not necessary for the request, malware, or content that you are not authorized to process.
            </p>
            <p>
              Uploaded files may be processed by AviaGov systems and configured AI service providers to provide the requested answer, generate drafts, estimate usage, maintain safety and security, and operate the service. FlyMate may distinguish uploaded-file context from retrieved ICAO, manual, or approved reference evidence, and user-uploaded content is not automatically treated as authoritative or approved evidence.
            </p>
            <p>
              File upload access may be limited to active or trialing paid plans, and AviaGov may restrict file types, file sizes, usage volumes, or availability to protect service reliability, safety, and security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Account Deletion and Data Retention</h2>
            <p>
              Users who wish to delete a FlyMate or AviaGov account must email <strong>support@aviagov.com</strong> from the account email address and clearly request account deletion. AviaGov may require identity verification before acting on the request.
            </p>
            <p>
              Account deletion is subject to applicable legal, contractual, billing, security, fraud-prevention, audit, dispute-resolution, and compliance retention obligations. AviaGov may retain data for as long as reasonably necessary to satisfy those obligations, protect the platform, enforce these Terms, resolve disputes, and preserve required operational or compliance evidence.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Communications and Product Updates</h2>
            <p>
              When you start a FlyMate session or submit a lead form, acceptance includes consent to receive AviaGov email updates, marketing communications, and product updates in addition to these Terms of Service and the Privacy Policy.
            </p>
            <p>
              This combined acceptance is required to use the FlyMate service. By proceeding, you agree both to the legal terms of service use and to AviaGov follow-up communications related to marketing and product updates.
            </p>
          </section>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
